/**
 * Extract country outlines as path polylines for globe.gl `pathsData`.
 * Lines are cheap (no polygon tessellation / hover hit-tests).
 * GeoJSON coords are [lng, lat].
 */

export type BorderPath = {
  code: string;
  /** Sequence of [lng, lat] vertices for one ring. */
  coords: Array<[number, number]>;
};

type GeoGeometry = {
  type?: string;
  coordinates?: unknown;
};

function ringToCoords(ring: unknown): Array<[number, number]> | null {
  if (!Array.isArray(ring) || ring.length < 2) return null;
  const coords: Array<[number, number]> = [];
  for (const pt of ring) {
    if (!Array.isArray(pt) || typeof pt[0] !== "number" || typeof pt[1] !== "number") {
      continue;
    }
    coords.push([pt[0], pt[1]]);
  }
  return coords.length >= 2 ? coords : null;
}

/** Exterior rings only (skip holes). One path per ring. */
export function geojsonToBorderPaths(
  features: ReadonlyArray<{
    properties: { ISO_A2?: string };
    geometry: unknown;
  }>,
): BorderPath[] {
  const paths: BorderPath[] = [];
  for (const feat of features) {
    const code = feat.properties.ISO_A2;
    if (!code || code === "-99" || code === "AQ") continue;
    const geom = feat.geometry as GeoGeometry | null;
    if (!geom?.type || !geom.coordinates) continue;

    if (geom.type === "Polygon") {
      const rings = geom.coordinates as unknown[];
      const outer = ringToCoords(rings[0]);
      if (outer) paths.push({ code, coords: outer });
      continue;
    }

    if (geom.type === "MultiPolygon") {
      const polys = geom.coordinates as unknown[];
      for (const poly of polys) {
        if (!Array.isArray(poly)) continue;
        const outer = ringToCoords(poly[0]);
        if (outer) paths.push({ code, coords: outer });
      }
    }
  }
  return paths;
}
